/**
 * Calendar dates for money screens. A billing date is a DAY in the gym's timezone
 * (CLAUDE.md "Dates are in the gym's timezone"), carried as an ISO `YYYY-MM-DD` string
 * and never turned into a `Date` at local midnight, which would shift it by a day for
 * anyone east or west of the gym.
 */

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

export interface DayParts {
  year: number;
  /** 1–12 */
  month: number;
  day: number;
}

export function parseDay(iso: string): DayParts | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  if (!match) return null;
  return { year: Number(match[1]), month: Number(match[2]), day: Number(match[3]) };
}

export function toIsoDay({ year, month, day }: DayParts): string {
  return `${String(year).padStart(4, '0')}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

function daysInMonth(year: number, month: number): number {
  return new Date(Date.UTC(year, month, 0)).getUTCDate();
}

/** Days since 1970-01-01 for a calendar day (UTC arithmetic: no DST, no local zone). */
function dayNumber({ year, month, day }: DayParts): number {
  return Math.round(Date.UTC(year, month - 1, day) / 86_400_000);
}

function fromDayNumber(n: number): DayParts {
  const d = new Date(n * 86_400_000);
  return { year: d.getUTCFullYear(), month: d.getUTCMonth() + 1, day: d.getUTCDate() };
}

/** `iso + days`. */
export function addDays(iso: string, days: number): string {
  const parts = parseDay(iso);
  if (!parts) return iso;
  return toIsoDay(fromDayNumber(dayNumber(parts) + days));
}

/**
 * `iso + months`, the way Postgres does `date + interval 'n months'`: the day is kept,
 * or clamped to the last day of a shorter month (31 Jan + 1 month = 28/29 Feb). Always
 * add to the ANCHOR, never step month by month, or 31 Jan → 28 Feb → 28 Mar drifts.
 */
export function addMonths(iso: string, months: number): string {
  const parts = parseDay(iso);
  if (!parts) return iso;
  const index = parts.year * 12 + (parts.month - 1) + months;
  const year = Math.floor(index / 12);
  const month = (index % 12) + 1;
  return toIsoDay({ year, month, day: Math.min(parts.day, daysInMonth(year, month)) });
}

/** Whole days from `a` to `b` (b − a). */
export function daysBetween(a: string, b: string): number {
  const pa = parseDay(a);
  const pb = parseDay(b);
  if (!pa || !pb) return 0;
  return dayNumber(pb) - dayNumber(pa);
}

/** Today in the gym's timezone, as `YYYY-MM-DD`. */
export function gymToday(timezone: string | null | undefined, now: Date = new Date()): string {
  try {
    return new Intl.DateTimeFormat('en-CA', {
      timeZone: timezone || 'Asia/Karachi',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).format(now);
  } catch {
    return now.toISOString().slice(0, 10);
  }
}

/** A timestamp's calendar day in the gym's timezone. */
export function dayInZone(timestamp: string | Date, timezone: string | null | undefined): string {
  return gymToday(timezone, typeof timestamp === 'string' ? new Date(timestamp) : timestamp);
}

/**
 * `Mon 20 Oct`; the year only when it is not this year (`Wed 5 Mar 2025`), per
 * DESIGN.md §2.5. `withWeekday: false` gives `20 Oct`.
 */
export function formatDay(
  iso: string | null | undefined,
  options: { today?: string; withWeekday?: boolean; fallback?: string } = {},
): string {
  if (!iso) return options.fallback ?? '—';
  const parts = parseDay(iso);
  if (!parts) return options.fallback ?? '—';
  const weekday = WEEKDAYS[new Date(Date.UTC(parts.year, parts.month - 1, parts.day)).getUTCDay()];
  const thisYear = options.today ? parseDay(options.today)?.year : new Date().getFullYear();
  const base = `${parts.day} ${MONTHS[parts.month - 1]}`;
  const withYear = parts.year === thisYear ? base : `${base} ${parts.year}`;
  return options.withWeekday === false ? withYear : `${weekday} ${withYear}`;
}

/** `October 2026` for a `YYYY-MM` or `YYYY-MM-DD`. */
export function formatMonth(iso: string): string {
  const parts = parseDay(iso.length === 7 ? `${iso}-01` : iso);
  if (!parts) return iso;
  const names = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September',
    'October', 'November', 'December'];
  return `${names[parts.month - 1]} ${parts.year}`;
}

/** `5:40 pm` for a timestamp, in the gym's timezone. */
export function formatTime(timestamp: string | Date, timezone: string | null | undefined): string {
  try {
    return new Intl.DateTimeFormat('en-GB', {
      timeZone: timezone || 'Asia/Karachi',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    })
      .format(typeof timestamp === 'string' ? new Date(timestamp) : timestamp)
      .replace(/\s?([ap])\.?m\.?$/i, (_m, p: string) => ` ${p.toLowerCase()}m`);
  } catch {
    return '';
  }
}

/** "just now", "6 min ago", "2 h ago", "yesterday", "3 days ago" — for queue rows. */
export function timeAgo(timestamp: string | Date, now: Date = new Date()): string {
  const then = typeof timestamp === 'string' ? new Date(timestamp) : timestamp;
  const minutes = Math.max(0, Math.round((now.getTime() - then.getTime()) / 60_000));
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours} h ago`;
  const days = Math.round(hours / 24);
  if (days === 1) return 'yesterday';
  return `${days} days ago`;
}
